import React from "react";
import { alpha, Box, Tooltip, Typography } from "@mui/material";
import PlaceIcon from "@mui/icons-material/Place";
import dayjs from "dayjs";
import { eventColor } from "./eventColors";
import { StaffProfileLink } from "../../../Components/ProfileLinks";

// Short 12h range: "9:00–10:30 AM" when both halves share the same meridiem,
// otherwise "11:30 AM–1:00 PM". Falls back gracefully if either time is empty.
const fmt12Compact = (t?: string | null): { hm: string; ap: string } => {
  const [h, m] = String(t || "").split(":").map(Number);
  if (Number.isNaN(h)) return { hm: "", ap: "" };
  const ap = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return { hm: `${h12}:${String(m || 0).padStart(2, "0")}`, ap };
};

const shortTimeRange = (start?: string | null, end?: string | null): string => {
  const s = fmt12Compact(start);
  const e = fmt12Compact(end);
  if (!s.hm && !e.hm) return "";
  if (!e.hm) return `${s.hm} ${s.ap}`;
  if (!s.hm) return `${e.hm} ${e.ap}`;
  return s.ap === e.ap ? `${s.hm}–${e.hm} ${e.ap}` : `${s.hm} ${s.ap}–${e.hm} ${e.ap}`;
};

const modeLabel = (m?: string | null) =>
  m === "Online" ? "Online" : m === "InPerson" || m === "Offline" ? "In Person" : m || "—";

// Prefer the real grade→subject grouping when the backend supplied it. Kept
// short — grades and their subjects are joined with "/", groups with " · ".
// Falls back to the row's className when no grouping is available.
const compactGradeSubjects = (row: any): string => {
  const gs = row?.gradeSubjects;
  if (Array.isArray(gs) && gs.length) {
    const parts = gs
      .map((g: any) => {
        const gname = typeof g?.grade === "string" ? g.grade : g?.grade?.name || "";
        const subs = Array.isArray(g?.subjects)
          ? g.subjects.map((s: any) => (typeof s === "string" ? s : s?.name || "")).filter(Boolean)
          : [];
        if (!gname && subs.length === 0) return "";
        const gShort = gname.replace(/^grade\s*/i, "G");
        return subs.length ? `${gShort} ${subs.join("/")}` : gShort;
      })
      .filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  return row?.className || "";
};

export type MonthEventDensity = "compact" | "medium" | "full";

export interface MonthEventCardProps {
  row: any;
  onClick?: (row: any) => void;
  compact?: boolean;
  /**
   * Responsive density controls how many lines the card renders.
   * - "full"    (default, wide desktop ≥1200): time, staff, grade/subject, enrolled·mode  (4 lines)
   * - "medium"  (900-1199):                     time, staff, enrolled·mode                (3 lines)
   * - "compact" (<900):                         start time, staff, enrolled               (2-3 lines)
   * Full information always available via the tooltip and details drawer.
   */
  density?: MonthEventDensity;
}

const MonthEventCard: React.FC<MonthEventCardProps> = ({ row, onClick, compact, density = "full" }) => {
  const c = eventColor(row);
  const timeText = shortTimeRange(row?.startTime, row?.endTime);
  const staff = row?.staffName || "Unassigned";
  const gs = compactGradeSubjects(row);
  const enrolled = Number(row?.studentCount ?? 0);
  const cap = row?.capacity != null && Number(row.capacity) > 0 ? Number(row.capacity) : null;
  const enrollText = cap != null ? `${enrolled}/${cap}` : `${enrolled} student${enrolled === 1 ? "" : "s"}`;
  const mode = String(row?.classMode || "");
  const isInPerson = mode === "InPerson" || mode === "Offline";
  const trailing = isInPerson && row?.locationName ? row.locationName : modeLabel(mode);
  const line4 = `${enrollText} · ${trailing}`;

  const full = cap != null && enrolled >= cap;
  const noStudents = enrolled === 0;
  const isHybrid = mode === "Hybrid" || mode === "hybrid";
  const isCancelled = /cancel/i.test(String(row?.status || ""));

  const dateFull = row?.scheduledDate ? dayjs(row.scheduledDate).format("dddd, MMM D, YYYY") : "";
  const durationText = (() => {
    const [sh, sm] = String(row?.startTime || "").split(":").map(Number);
    const [eh, em] = String(row?.endTime || "").split(":").map(Number);
    if ([sh, sm, eh, em].some(Number.isNaN)) return "";
    const mins = (eh * 60 + em) - (sh * 60 + sm);
    if (!Number.isFinite(mins) || mins <= 0) return "";
    const h = Math.floor(mins / 60), m = mins % 60;
    return h && m ? `${h}h ${m}m` : h ? `${h}h` : `${m}m`;
  })();

  const gsTooltip = (() => {
    const g = row?.gradeSubjects;
    if (Array.isArray(g) && g.length) {
      return g.map((x: any) => {
        const gn = typeof x?.grade === "string" ? x.grade : x?.grade?.name || "";
        const subs = Array.isArray(x?.subjects)
          ? x.subjects.map((s: any) => (typeof s === "string" ? s : s?.name || "")).filter(Boolean).join(", ")
          : "";
        return `${gn}${subs ? `: ${subs}` : ""}`;
      }).join("\n");
    }
    return row?.subjectName || row?.className || "";
  })();

  const tooltip = (
    <Box sx={{ whiteSpace: "pre-line", fontSize: 12, lineHeight: 1.45 }}>
      {dateFull && <div><b>{dateFull}</b></div>}
      {timeText && <div>{timeText}{durationText ? ` (${durationText})` : ""}</div>}
      <div>Staff: {staff}</div>
      {gsTooltip && <div>Subjects:{"\n"}{gsTooltip}</div>}
      <div>Enrolled: {enrollText}{full ? " (Full)" : ""}</div>
      <div>Mode: {modeLabel(mode)}</div>
      {row?.locationName && <div>Location: {row.locationName}</div>}
      {row?.classType && <div>Type: {row.classType}</div>}
      {row?.status && <div>Status: {row.status}</div>}
    </Box>
  );

  const handleActivate = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation?.();
    onClick?.(row);
  };

  const ariaLabel = [
    dateFull,
    timeText,
    staff && `with ${staff}`,
    gs && `for ${gs}`,
    enrollText,
    modeLabel(mode),
    row?.locationName && `at ${row.locationName}`,
    full ? "Full" : null,
  ].filter(Boolean).join(", ");

  // At compact density we show start time only (no end) to keep the first
  // line short, and drop the grade/subjects line entirely — details still live
  // in the tooltip and the drawer.
  const compactTime = (() => {
    const s = fmt12Compact(row?.startTime);
    return s.hm ? `${s.hm} ${s.ap}` : "";
  })();
  const showGradeLine = density === "full" && Boolean(gs);
  const showLine4    = density !== "compact";
  const showEnrollOnly = density === "compact";
  const timeToShow = density === "compact" ? compactTime : timeText;

  const isFull = density === "full";
  // Compact premium card — colorful + readable but sized to fit inside the
  // previous compact month row (target ~72–82px total: time + staff +
  // grade + count·location + one badge row). Padding stays tight.
  const cardPadX = isFull ? 0.85 : 0.7;
  const cardPadY = isFull ? 0.5 : (compact ? 0.35 : 0.55);
  const leftBorderW = isFull ? 4 : 3;
  const cardBg = isFull ? alpha(c.border, 0.14) : c.bg;

  // Status badge — spec's palette (blue/green/red/yellow) but we only emit
  // one badge per card so the row never gets crowded.
  const statusBadge = (() => {
    if (isCancelled) return { label: "Cancelled", bg: "#ef4444" };
    if (full) return { label: "Full", bg: "#ef4444" };
    if (noStudents) return { label: "No students", bg: "rgba(245,158,11,0.95)" };
    return null;
  })();

  return (
    <Tooltip title={tooltip} arrow placement="top" enterDelay={300}>
      <Box
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleActivate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleActivate(e);
          }
        }}
        sx={{
          mt: 0.4,
          px: cardPadX,
          py: cardPadY,
          borderRadius: isFull ? "9px" : "7px",
          borderLeft: `${leftBorderW}px solid ${c.border}`,
          bgcolor: cardBg,
          color: c.text,
          cursor: "pointer",
          // minWidth:0 lets the card shrink inside the grid cell (grid children
          // default to min-width:auto = intrinsic width, which is the classic
          // cause of tracks blowing past `1fr`). maxWidth:100% is belt-and-braces.
          minWidth: 0,
          maxWidth: "100%",
          // No overflow:hidden on the card itself — that was clipping wrapped
          // badges and the location line. Content is short enough to fit; the
          // parent cell handles any extreme overflow via +N more.
          position: "relative",
          boxShadow: isFull
            ? `0 2px 6px ${alpha(c.border, 0.18)}, 0 1px 2px rgba(15,23,42,0.04)`
            : "0 1px 1px rgba(15,23,42,0.03)",
          transition: "transform .12s ease, box-shadow .12s ease, filter .12s ease",
          "&:hover": {
            filter: "brightness(0.98)",
            transform: "translateY(-1px)",
            boxShadow: isFull
              ? `0 8px 18px -6px ${alpha(c.border, 0.35)}, 0 2px 4px rgba(15,23,42,0.06)`
              : `0 6px 14px -8px ${c.border}`,
          },
          "&:focus-visible": {
            outline: `2px solid ${c.border}`,
            outlineOffset: 1,
          },
        }}
      >
        {timeToShow && (
          <Typography sx={{
            fontSize: isFull ? 11.5 : 11,
            fontWeight: 700,
            lineHeight: 1.25,
            color: c.border,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {timeToShow}
          </Typography>
        )}
        {/* Staff name — the anchor identity. Single line + ellipsis so the
            card stays compact; the full name is always in the tooltip.
            Wrapped in <StaffProfileLink> so clicking the name opens the
            staff profile without also opening the session drawer
            (stopPropagation lives inside the link). Ctrl/Cmd + click
            preserves native "open in new tab". */}
        <StaffProfileLink
          id={row?.staff_id ?? row?.staff?.id}
          name={staff}
          sx={{
            display: "block",
            fontSize: isFull ? 12 : 11.5,
            fontWeight: 800,
            lineHeight: 1.2,
            letterSpacing: "-0.005em",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {staff}
        </StaffProfileLink>
        {showGradeLine && (
          <Typography sx={{
            fontSize: isFull ? 10.5 : 10.5,
            lineHeight: 1.2,
            opacity: 0.85,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}>
            {gs}
          </Typography>
        )}
        {/* Full density (compact premium): one info line combining student
            count + location (colored + place icon), then a wrapping badge
            row when needed. This is the layout the spec asks for and it
            keeps the whole card in the ~72–82px range. */}
        {isFull && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25, minWidth: 0 }}>
              <Typography sx={{
                fontSize: 10.5,
                lineHeight: 1.2,
                opacity: 0.9,
                fontWeight: 600,
                whiteSpace: "nowrap",
              }}>
                {enrollText}
              </Typography>
              {row?.locationName && (
                <>
                  <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, opacity: 0.6 }}>·</Typography>
                  <PlaceIcon sx={{ fontSize: 11, color: c.border, flexShrink: 0 }} />
                  <Typography sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: c.border,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    minWidth: 0,
                  }}>
                    {row.locationName}
                  </Typography>
                </>
              )}
            </Box>
            {(isHybrid || statusBadge) && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.35, mt: 0.3 }}>
                {isHybrid && (
                  <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.55, py: 0.1, borderRadius: "4px", bgcolor: "rgba(139,92,246,0.9)", color: "#fff", lineHeight: 1.4, letterSpacing: 0.2 }}>
                    Hybrid
                  </Box>
                )}
                {statusBadge && (
                  <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.55, py: 0.1, borderRadius: "4px", bgcolor: statusBadge.bg, color: "#fff", lineHeight: 1.4, letterSpacing: 0.2 }}>
                    {statusBadge.label}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
        {/* Medium density: single horizontal line with truncation — preserves
            the old compact behaviour when the calendar column is narrow. */}
        {showLine4 && !isFull && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, opacity: 0.9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>
              {line4}
            </Typography>
            {isHybrid && (
              <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.55, borderRadius: "4px", bgcolor: "rgba(139,92,246,0.85)", color: "#fff", lineHeight: 1.4, letterSpacing: 0.2 }}>
                Hybrid
              </Box>
            )}
            {statusBadge && (
              <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.55, borderRadius: "4px", bgcolor: statusBadge.bg, color: "#fff", lineHeight: 1.4 }}>
                {statusBadge.label}
              </Box>
            )}
          </Box>
        )}
        {showEnrollOnly && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Typography sx={{ fontSize: 10.5, lineHeight: 1.2, opacity: 0.9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, minWidth: 0 }}>
              {enrollText}
            </Typography>
            {statusBadge && (
              <Box component="span" sx={{ fontSize: 9, fontWeight: 800, px: 0.55, borderRadius: "4px", bgcolor: statusBadge.bg, color: "#fff", lineHeight: 1.4 }}>
                {statusBadge.label}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default MonthEventCard;
